import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useSEO, breadcrumb, faqPage } from "@/lib/useSEO";
import { useStudio } from "@/context/StudioContext";

const PILLARS = [
  {
    n: "01",
    title: "Considered curation",
    body:
      "We source a tight edit of the world's finest timber, carpet, tile and stone. Every piece is chosen for craft, provenance and how it holds its beauty for decades — not seasons.",
  },
  {
    n: "02",
    title: "One studio, one language",
    body:
      "Four elements under one roof means every surface is specified in conversation with the others. Grain speaks to weave, weave to vein, vein to grout line.",
  },
  {
    n: "03",
    title: "Quiet, senior service",
    body:
      "You will always deal with a senior studio member. No showroom shuffle, no upselling — just clear specification advice built for architects, designers and homeowners who already know what excellence looks like.",
  },
];

// FAQ schema — helps Google surface rich answers in search results.
const ABOUT_FAQ = faqPage([
  {
    q: "Where is My Floor Studio located?",
    a: "My Floor Studio is at 184 Victoria Road, Drummoyne NSW 2047 — a short drive from Sydney CBD, the Inner West, the Lower North Shore and the Eastern Suburbs. Open Monday to Saturday, 9:00–17:00.",
  },
  {
    q: "What kinds of flooring does My Floor Studio specify?",
    a: "Four elements: engineered and solid timber flooring (wide plank oak, herringbone, chevron, Versailles parquet), luxury wool and blended carpets, natural stone (marble, limestone, travertine, bluestone, terrazzo), and premium porcelain and ceramic tile.",
  },
  {
    q: "Do you work with architects and interior designers?",
    a: "Yes. My Floor Studio runs a dedicated trade portal for architects, designers and builders, including on-site consultations, sample kits, sample boards for client presentations, and quantity/specification support across residential and commercial projects.",
  },
  {
    q: "Can I book a private consultation at the Drummoyne studio?",
    a: "Absolutely. You can book a private appointment through the site — appointments are 45 minutes with a senior studio member who will walk you through the collection tailored to your project.",
  },
  {
    q: "Do you deliver flooring samples across Sydney and Australia?",
    a: "Yes. We dispatch curated sample kits nationally. Sydney requests are usually delivered within 2–3 business days; regional and interstate orders 3–7 business days depending on carrier.",
  },
]);

export default function About() {
  useSEO({
    title:
      "About · The Studio Behind My Floor Studio — Drummoyne, Sydney",
    description:
      "A Drummoyne selection studio bringing Timber, Carpet, Tile and Stone under one roof. Considered curation, senior specification advice, and a decade of Sydney's finest luxury flooring installations.",
    path: "/about",
    keywords:
      "about my floor studio, Drummoyne flooring studio, luxury flooring Sydney, architect flooring specification, interior designer flooring, senior specification advice",
    imageAlt:
      "Inside the My Floor Studio Drummoyne showroom — a curator's material console",
    jsonLd: [
      ABOUT_FAQ,
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
      ]),
    ],
  });
  const { openBooking } = useStudio();

  return (
    <div className="min-h-screen bg-[#121315] grain">
      <Nav />

      {/* HERO */}
      <section className="pt-40 md:pt-48 pb-24 md:pb-32">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-8">About the Studio</p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-5xl sm:text-6xl md:text-7xl xl:text-8xl leading-[0.96]"
            >
              The masterpiece<br />of connection
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 text-[#D6D1CA]/85 text-lg md:text-xl font-body max-w-xl leading-relaxed"
            >
              My Floor Studio is a Drummoyne-based selection studio for architects, designers,
              builders and homeowners who understand that the floor is not the last decision —
              it is the first.
            </motion.p>
          </div>

          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-[4/5] overflow-hidden"
              style={{ boxShadow: "0 40px 100px rgba(0,0,0,0.4)" }}
            >
              <img
                src="/about_studio_scene.jpg"
                alt="A curator's material console — oak, marble, wool and porcelain samples arranged on a linen-covered table in a warm studio interior"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(18,19,21,0) 40%, rgba(18,19,21,0.6) 100%)" }} />
              <div className="absolute inset-3 border border-[#F5F4F0]/12 pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="border-t border-[#8C7355]/20 py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-6">Philosophy</p>
            <h2 className="font-serif-display text-[#F5F4F0] font-light tracking-tight text-4xl sm:text-[2.75rem] md:text-5xl leading-[1.02]">
              Four elements.<br />One foundation.
            </h2>
          </div>
          <div className="md:col-span-8 space-y-6 text-[#D6D1CA]/85 font-body text-base md:text-lg leading-relaxed max-w-2xl">
            <p>
              Most flooring suppliers ask you to shop by product. We ask you to think in
              rooms. A herringbone oak entry that hands off to a honed limestone corridor,
              which softens into a wool-loop bedroom. The materials are the words — the
              transitions are the sentence.
            </p>
            <p>
              We founded the studio because that conversation was happening on three
              different floors of three different showrooms across the city. Timber people
              talking about timber. Tile people talking about tile. Nobody talking about
              the whole house.
            </p>
            <p>
              The result is a selection studio built like an architectural library:
              quiet, precise, and organised for people who care about the entire foundation,
              not just one plank of it.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-t border-[#8C7355]/20 py-24 md:py-32 bg-[#0f1012]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-14">How we work</p>
          <div className="grid md:grid-cols-3 gap-12 md:gap-16">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="font-accent text-[#C9A876]/50 text-sm tracking-[0.3em]">{p.n}</span>
                <h3 className="mt-5 font-serif-display text-[#F5F4F0] text-3xl md:text-4xl font-light tracking-tight leading-tight">
                  {p.title}
                </h3>
                <p className="mt-6 text-[#D6D1CA]/75 text-base font-body leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STUDIO IMAGE STRIP */}
      <section className="border-t border-[#8C7355]/20 py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-6">The Studio</p>
            <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-4xl sm:text-[2.75rem] md:text-5xl leading-[1.02]">
              A Private Gallery<br />on Victoria Road
            </h2>
            <div className="mt-8 space-y-5 text-[#D6D1CA]/80 font-body text-base md:text-lg leading-relaxed max-w-lg">
              <p>
                Our Drummoyne studio is not a warehouse. It is a small, considered room —
                large-format samples laid on drafting tables, wide-plank offcuts on the
                floor, stone slabs in daylight. Everything you would want to touch, at the
                scale you would actually install it.
              </p>
              <p>
                Appointments are one-on-one with a senior curator. Bring your plans, your
                mood board or nothing at all. We will walk the house with you.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-4 max-w-md">
              {[
                { l: "Address", v: "184 Victoria Road, Drummoyne NSW 2047" },
                { l: "Hours", v: "Monday - Friday: 9:00am - 5:00pm\nSaturday: By Appointment\nSunday: Closed" },
                { l: "Founded", v: "Sydney, in the studio behind the studio" },
                { l: "Serving", v: "Sydney, the Central Coast, the Southern Highlands" },
              ].map((r) => (
                <div key={r.l} className="border-t border-[#8C7355]/18 pt-3">
                  <p className="text-[#C9A876] text-[9px] tracking-[0.3em] uppercase">{r.l}</p>
                  <p className="text-[#F5F4F0] text-sm mt-1.5 font-body leading-snug whitespace-pre-line">{r.v}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-6 order-1 md:order-2">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2 }}
              className="relative aspect-[5/6] overflow-hidden"
            >
              <img
                src="/about_visit_room.jpg"
                alt="A serene sitting room corner with a caramel leather wing chair on a Taj Mahal quartzite floor, tall arched window and burnished bronze floor lamp"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-3 border border-[#F5F4F0]/10 pointer-events-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#8C7355]/20 py-32 text-center">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-8">Visit the studio</p>
          <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-5xl sm:text-6xl md:text-7xl leading-[0.98] max-w-4xl mx-auto">
            Request a private<br />consultation
          </h2>
          <p className="mt-10 text-[#D6D1CA]/75 font-body max-w-md mx-auto">
            One hour, one senior curator, and every material you need to specify a considered home.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <button
              data-testid="about-book-btn"
              onClick={openBooking}
              className="btn-bronze rounded-full px-10 py-4 text-[11px] tracking-[0.22em] uppercase font-medium"
            >
              Request Appointment
            </button>
            <Link
              to="/contact"
              data-testid="about-contact-btn"
              className="link-underline text-[#D6D1CA] text-[11px] tracking-[0.22em] uppercase"
            >
              Send us a note →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
